import type { ErrorLike } from '@apollo/client';
import type { Duration } from 'date-fns';

import type {
  Option,
  RecipeRunTotalsFragment
} from '../__generated__/apollo-hooks';
import { getCurrentLocale } from './i18n.helper';
import { parseISODuration } from './time.helper';

const DEFAULT_DURATION_OBJECT = {
  years: 0,
  months: 0,
  weeks: 0,
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0
};
export const summarizeTimeSavings = (
  results: Pick<RecipeRunTotalsFragment, 'timeSavings'>[]
): Duration => {
  const duration = results
    .filter(Boolean)
    .map((s) =>
      s.timeSavings ? parseISODuration(s.timeSavings) : DEFAULT_DURATION_OBJECT
    )
    .reduce(
      (a, b) => {
        const val = {};
        Object.keys(a).forEach((k) => {
          // biome-ignore lint/suspicious/noAssignInExpressions: migration from eslint
          return (val[k] = Number(a[k]) + (b[k] ? Number(b[k]) : 0));
        });
        return val;
      },
      {
        years: 0,
        months: 0,
        weeks: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      }
    );

  if (duration.seconds >= 60) {
    duration.minutes += Math.ceil(duration.seconds / 60);
    duration.seconds = duration.seconds % 60;
  }
  if (duration.minutes >= 60) {
    duration.hours += Math.ceil(duration.minutes / 60);
    duration.minutes = duration.minutes % 60;
  }

  return duration;
};

/**
 * Formats an ISO time-savings duration as whole hours (e.g. "5 hours",
 * residual minutes are dropped), or as a fractional hour rounded to 1 decimal
 * place when under an hour (e.g. "0.5 hours"). Returns null for missing or
 * sub-minute savings so callers can omit the value without comparing strings.
 *
 * Uses Intl.NumberFormat instead of date-fns because we need locale-aware
 * number formatting, not translated duration words.
 */
export const formatTimeSavingsHours = (timeSavings?: string): string | null => {
  const summarizedSavings = summarizeTimeSavings([{ timeSavings }]);

  if (summarizedSavings?.hours >= 1) {
    // If there are hours only show how many
    return `${Intl.NumberFormat(getCurrentLocale()).format(
      summarizedSavings.hours
    )} ${summarizedSavings.hours > 1 ? 'hours' : 'hour'}`;
  }
  if (summarizedSavings?.minutes >= 1) {
    // If there are no hours but there are minutes show fractional hour rounded to 1 decimal place
    return `${Intl.NumberFormat(getCurrentLocale(), {
      minimumIntegerDigits: 1,
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(summarizedSavings.minutes / 60)} hours`;
  }
  return null;
};

export const recipeNotFoundError = (
  id: string,
  error: ErrorLike
): string | false => {
  const recipeNotFoundErrors = [
    `Recipe run ${id} does not exist.`,
    `Recipe run not found: ${id} (or recipe not found for run).`
  ];

  for (let i = 0; i < recipeNotFoundErrors.length; i++) {
    if (error?.message.includes(recipeNotFoundErrors[i])) {
      return recipeNotFoundErrors[i];
    }
  }

  return false;
};

export const safePrintRecipeOptionValue = (optionValue: unknown): string => {
  if (Array.isArray(optionValue)) {
    return optionValue.join(', ');
  }

  return String(optionValue).replace(',', ', ');
};

// Only reads `value`, so accept anything option-shaped (e.g. RecipeOptionValue,
// which lacks the other Option fields) rather than requiring a full Option.
export const isValidOptionValue = ({ value }: Pick<Option, 'value'>) =>
  value !== null &&
  value !== '' &&
  value !== undefined &&
  !(Array.isArray(value) && value?.length === 0);
